import { useCallback } from 'react';
import { Menu } from '@shared/components/Menu';
import { View } from '@shared/components/View';
import { MAX_BEFORE_TRUNCATION } from '@shared/scenes/chat/thread/components/message/constants';
import {
  SeenByCountText,
  SeenByCountTextProps,
} from '@shared/scenes/chat/thread/components/seenBy/SeenByCountText';
import { SeenByImages } from '@shared/scenes/chat/thread/components/seenBy/SeenByImages';
import { SeenByMenuContent } from '@shared/scenes/chat/thread/components/seenBy/SeenByMenuContent';
import { SeenWithoutCountText } from '@shared/scenes/chat/thread/components/seenBy/SeenWithoutCountText';
import { API } from '@shared/types/api';
import styles from './SeenBy.module.css';

interface SeenByProps {
  users: API.ShortUserFragment[];
  sender?: API.ShortUserFragment;
  showImages?: boolean;
  isSeenByCountHidden?: boolean;
  onShowDetails?: () => void;
  color?: SeenByCountTextProps['color'];
  align: 'right' | 'left';
}

export function SeenBy({
  onShowDetails,
  users,
  sender,
  showImages,
  isSeenByCountHidden,
  color,
  align = 'left',
}: SeenByProps) {
  const menuAlign = align === 'right' ? 'left' : 'right';

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onShowDetails?.();
      }
    },
    [onShowDetails],
  );

  return (
    <Menu align={menuAlign} onOpenChange={handleOpenChange}>
      <Menu.Trigger>
        <View row>
          <SeenByTriggerContent
            showImages={showImages}
            isSeenByCountHidden={isSeenByCountHidden}
            color={color}
            users={users}
          />
        </View>
      </Menu.Trigger>
      <Menu.Content className={styles.seenByMenu}>
        <SeenByMenuContent users={users} sender={sender} />
      </Menu.Content>
    </Menu>
  );
}

function SeenByTriggerContent({
  showImages,
  isSeenByCountHidden,
  color,
  users,
}: Omit<SeenByProps, 'align' | 'sender' | 'onShowDetails'>) {
  if (showImages) {
    return (
      <SeenByImages
        users={users}
        isCountVisible={users.length > MAX_BEFORE_TRUNCATION && !isSeenByCountHidden}
      />
    );
  }

  if (isSeenByCountHidden) {
    return <SeenWithoutCountText color={color} />;
  }

  return <SeenByCountText count={users.length} color={color} />;
}
